import {useCallback} from "react";

import {keepPreviousData, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";

import {
  ActionRecordModel,
  ActionTypeModel,
  AssistantActionAttachmentTypeModel,
  CustomActionRecordModel,
  ListModel,
} from "@/api/models";
import {
  useCreateAction,
  useUpdateAction,
  useDeleteAction,
  useGetActionList,
  useCreateActionFromTemplate,
  getGetActionListSummaryQueryKey,
} from "@/api/services/queries";
import {actionTransform} from "@/api/transform/actions";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {useToast} from "./useToast";

export const useActionListManage = <
  T extends ActionRecordModel = ActionRecordModel,
>({
  actionType,
  paginationSize = 10,
  externalPagination,
  attachmentType,
  search,
}: {
  actionType: ActionTypeModel;
  paginationSize?: number;
  externalPagination?: {
    pagination: {
      pageIndex: number;
      pageSize: number;
    };
    setPagination: (pagination: {pageIndex: number; pageSize: number}) => void;
  };
  attachmentType?: AssistantActionAttachmentTypeModel;
  search?: string;
}) => {
  const queryClient = useQueryClient();
  const selectedWorkspace = FlowBuilderSettings.workspaceId;

  const pagination = externalPagination?.pagination || {
    pageIndex: 0,
    pageSize: paginationSize,
  };
  const setPagination = externalPagination?.setPagination || (() => {});

  const getActionsResponse = useGetActionList(
    {
      workspace_id: selectedWorkspace,
      page_size:
        actionType === ActionTypeModel.CUSTOM && attachmentType
          ? 99
          : pagination.pageSize,
      page_number:
        actionType === ActionTypeModel.CUSTOM && attachmentType
          ? 1
          : pagination.pageIndex + 1,
      action_type: actionType,
      search: search?.trim() || undefined,
    } as any,
    {
      query: {
        queryKey: [
          "actions",
          selectedWorkspace,
          actionType,
          pagination,
          attachmentType,
          search,
        ],
        select: data => {
          if (!data) {
            return undefined;
          }

          const listModel = actionTransform[actionType].makeListModel(
            data,
          ) as ListModel<T>;

          if (
            actionType === ActionTypeModel.CUSTOM &&
            attachmentType &&
            listModel.records
          ) {
            const filteredRecords = listModel.records.filter(action => {
              const customAction = action as unknown as CustomActionRecordModel;
              if (
                attachmentType === AssistantActionAttachmentTypeModel.BEFORE
              ) {
                return customAction.run_action_before_call_start;
              }
              if (
                attachmentType === AssistantActionAttachmentTypeModel.DURING
              ) {
                return !customAction.run_action_before_call_start;
              }
              return true;
            });

            const paginatedRecords = filteredRecords.slice(
              pagination.pageIndex * pagination.pageSize,
              (pagination.pageIndex + 1) * pagination.pageSize,
            );

            return {
              ...listModel,
              pagination: {
                ...listModel.pagination,
                totalCount: filteredRecords.length,
              },
              records: paginatedRecords,
            };
          }

          return listModel;
        },
        placeholderData: keepPreviousData,
      },
    },
  );

  const {mutateAsync: createAction} = useCreateAction();
  const {mutateAsync: updateAction} = useUpdateAction();
  const {mutateAsync: createActionFromTemplate} = useCreateActionFromTemplate();

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["actions", selectedWorkspace, actionType],
    });
    queryClient.invalidateQueries({
      queryKey: getGetActionListSummaryQueryKey({
        workspace_id: selectedWorkspace,
      }),
    });
  }, [actionType, queryClient, selectedWorkspace]);

  const handleCreateAction = useCallback(
    async (
      data: Parameters<
        (typeof actionTransform)[typeof actionType]["makeCreateDto"]
      >[0],
    ) => {
      // TODO: fix this
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const dto = actionTransform[actionType].makeCreateDto(data);

      const action = await createAction({data: dto});
      refreshList();
      return action;
    },
    [actionType, createAction, refreshList],
  );

  const handleCreateFromTemplateAction = useCallback(
    async (slug: string) => {
      const action = await createActionFromTemplate({
        slug,
        data: {
          fields: {
            workspace_id: selectedWorkspace,
          },
        },
        params: {
          action_type: "custom",
        },
      });
      refreshList();
      return action;
    },
    [createActionFromTemplate, refreshList, selectedWorkspace],
  );

  const handleUpdateAction = useCallback(
    async (
      slug: string,
      data: Parameters<
        (typeof actionTransform)[typeof actionType]["makeUpdateDto"]
      >[0],
    ) => {
      // TODO: fix this
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const dto = actionTransform[actionType].makeUpdateDto(data);

      const action = await updateAction({
        data: dto,
        slug,
      });
      refreshList();
      return action;
    },
    [actionType, updateAction, refreshList],
  );

  const {mutateAsync: deleteAction} = useDeleteAction();
  const {errorToast} = useToast();
  const handleDeleteAction = useCallback(
    async (id: string) => {
      try {
        await deleteAction({slug: id});
        refreshList();
      } catch (e) {
        const error = e as AxiosError<{message?: string}>;
        errorToast(error.response?.data?.message || "Failed to delete action");
      }
    },
    [deleteAction, refreshList],
  );

  return {
    ...getActionsResponse,
    pagination,
    setPagination,
    handleCreateAction,
    handleCreateFromTemplateAction,
    handleUpdateAction,
    handleDeleteAction,
    actionType,
  };
};

export default useActionListManage;
